import styled from 'styled-components';
import { COLORS } from '../../../constants/radar';
import { getBenchDaysColor } from '../../../constants/benchTypes';

export const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 24px;
`;

export const HeaderStats = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const HeaderStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  position: relative;
  padding: 0 16px;
  min-height: 36px;

  .top {
    display: flex;
    align-items: baseline;
    gap: 6px;
  }

  .label {
    font-size: 11px;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }

  .value {
    font-size: 20px;
    font-weight: 700;
    color: ${COLORS.PRIMARY};
  }

  .sub {
    font-size: 9px;
    color: #94A3B8;
    line-height: 1;
  }

  & + &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 24px;
    background: #e0e0e0;
  }
`;

export const EmployeeRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s;
  gap: 6px;

  &:hover {
    background-color: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const EmployeeRowMain = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const PlacementActivitySummaryRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
  padding-left: 56px;

  .muted {
    font-style: italic;
    color: #94a3b8;
  }

  .pa-icon-button {
    display: inline-flex;
    align-items: center;
    padding: 2px;
    margin-left: 4px;
    color: #475569;
    cursor: pointer;
    border: none;
    background: transparent;
    border-radius: 4px;

    &:hover {
      background-color: #e2e8f0;
      color: ${COLORS.PRIMARY};
    }
  }

  .pa-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 1px 6px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 500;
  }

  .pa-more {
    font-size: 11px;
    font-weight: 600;
    color: #475569;
    margin-left: 4px;
  }
`;

export const EmployeeInfo = styled.div`
  flex: 1;
  min-width: 0;

  .name {
    font-size: 14px;
    font-weight: 500;
    color: ${COLORS.PRIMARY};
  }

  .name-link {
    display: inline-block;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }

  .detail {
    font-size: 12px;
    color: #64748B;
    margin-top: 2px;
  }
`;

export const BenchMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const DaysCell = styled.div`
  min-width: 80px;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: ${({ $days }) => getBenchDaysColor($days)};
`;

export const LoadMoreButton = styled.button`
  display: block;
  margin: 16px auto;
  padding: 8px 24px;
  background: #f1f5f9;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
    border-color: #94A3B8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SortButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #64748B;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: #f1f5f9;
    color: ${COLORS.PRIMARY};
  }

  svg {
    font-size: 12px;
  }
`;

export const TechBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  background-color: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
  margin: 1px 2px;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${({ $color }) => $color || '#94A3B8'};
    border: 1.5px solid ${({ $color }) => {
      // Darken the color by blending toward black
      if (!$color) { return '#1e293b'; }
      const hex = $color.replace('#', '');
      const r = Math.round(parseInt(hex.substring(0, 2), 16) * 0.55);
      const g = Math.round(parseInt(hex.substring(2, 4), 16) * 0.55);
      const b = Math.round(parseInt(hex.substring(4, 6), 16) * 0.55);
      return `rgb(${r}, ${g}, ${b})`;
    }};
    flex-shrink: 0;
  }
`;

export const TechBadgeMore = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 500;
  color: #334155;
  background: none;
  border: none;
  margin: 1px 2px;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.15s;

  &:hover {
    color: #1e293b;
  }
`;

export const TechBadgeLess = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 500;
  color: #334155;
  background: none;
  border: none;
  margin: 1px 2px;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.15s;

  &:hover {
    color: #1e293b;
  }
`;
